import moment from 'moment';

const MINUTES_PER_DAY = 1440;
const DAYS_PER_WEEK = 7;
const FIRST_WEEK = 1;
const FIRST_DAY_OF_WEEK = 1; // Monday

/**
 * The broadcast week start. Represents ***Monday 06:00 AM*** in minutes.
 */
const BROADCAST_WEEK_START_MINUTE = 360;

/**
 * The broadcast week end. Represents ***Monday 05:59 AM*** of
 * the next week in minutes.
 */
const BROADCAST_WEEK_END_MINUTE = 10439;

/**
 * Converts a date-like time value into minutes elapsed since midnight.
 *
 * @param {Date|string|moment.Moment} value - Time value to convert.
 * @returns {number|null} Minutes since midnight, or null for an invalid value.
 */
const getTimeInMinutes = (value) => {
  const date = moment(value, 'HH:mm:ss');

  if (!date.isValid()) return null;

  return moment
    .duration({
      hours: date.hours(),
      minutes: date.minutes(),
      seconds: date.seconds(),
    })
    .asMinutes();
};

/**
 * Converts a recurring week/day/time position into a sortable week offset.
 *
 * @param {Object} event - Event containing Week, DayOfWeek, and StartTime fields.
 * @returns {number|null} Minute offset from week 1 Monday midnight, or null for invalid data.
 */
const getEventStartInMinutes = (event) => {
  const week = Number(event.Week ?? event.week ?? FIRST_WEEK);
  const dayOfWeek = Number(event.DayOfWeek ?? event.dayOfWeek);
  const startInMins = getTimeInMinutes(event.StartTime ?? event.startTime);

  if (!Number.isInteger(week) || !Number.isInteger(dayOfWeek) || startInMins === null) {
    return null;
  }

  // Convert the recurring week/day/time position into one sortable minute
  // offset from the start of week 1.
  const weekOffsetMins = (week - FIRST_WEEK) * DAYS_PER_WEEK * MINUTES_PER_DAY;
  const dayOffsetMins = (dayOfWeek - FIRST_DAY_OF_WEEK) * MINUTES_PER_DAY;

  return weekOffsetMins + dayOffsetMins + startInMins;
};

/**
 * Calculates an event's end position, treating an earlier or equal end time as next-day.
 *
 * @param {Object} event - Event containing StartTime and EndTime fields.
 * @param {number} startMinute - Event start position in the recurring schedule.
 * @returns {number|null} Event end position, or null for an invalid end time.
 */
const getEventEndInMinutes = (event, startMinute) => {
  const endValue = event.EndTime ?? event.endTime;
  const endTime = getTimeInMinutes(endValue);

  if (endTime === null) return null;

  const startTime = getTimeInMinutes(event.StartTime ?? event.startTime);
  // An end time at or before the start time belongs to the following day.
  const crossesMidnight = endTime <= startTime;
  const duration = crossesMidnight ? endTime + MINUTES_PER_DAY - startTime : endTime - startTime;

  return startMinute + duration;
};

/**
 * Formats a minute offset as a time-of-day string.
 *
 * @param {number} minutes - Minute offset to format.
 * @returns {string} Time formatted as HH:mm:ss.
 */
const formatTime = (minutes) => moment.utc(0).add(minutes, 'minutes').format('HH:mm:ss');

// Convert a continuous minute offset back into the CA week/day/time fields.
/**
 * Converts a minute offset into the CA week/day/time representation.
 *
 * @param {number} minute - Minute offset from week 1 Monday midnight.
 * @returns {{week: number, dayOfWeek: number, time: string}} CA position.
 */
const getBlockPosition = (minute) => {
  const dayIndex = Math.floor(minute / MINUTES_PER_DAY);
  const normalizedWeekFromDayIndex = Math.floor(dayIndex / DAYS_PER_WEEK) + FIRST_WEEK;

  return {
    week: dayIndex === DAYS_PER_WEEK ? FIRST_WEEK : normalizedWeekFromDayIndex,
    dayOfWeek: (dayIndex % DAYS_PER_WEEK) + FIRST_DAY_OF_WEEK,
    time: formatTime(minute),
  };
};

/**
 * Splits a gap into placeholder blocks that each fit within one broadcast day.
 *
 * Broadcast days run from 06:00 through 05:59 the following day.
 *
 * @param {{start: number, end: number}} gap - Inclusive minute range to split.
 * @returns {Array<{start: number, end: number}>} Daily gap ranges.
 */
const splitGapIntoBroadcastDays = ({ start, end }) => {
  const blocks = [];
  let blockStart = start;

  // Split long gaps at each broadcast-day boundary so no placeholder extends
  // beyond 05:59. Broadcast days begin at 06:00, not at calendar midnight.
  while (blockStart < end) {
    const broadcastDayOffset = Math.floor(
      (blockStart - BROADCAST_WEEK_START_MINUTE) / MINUTES_PER_DAY,
    );
    const broadcastDayStart = BROADCAST_WEEK_START_MINUTE + broadcastDayOffset * MINUTES_PER_DAY;
    // The end minute is inclusive; the next placeholder starts at 06:00.
    const blockEnd = Math.min(broadcastDayStart + MINUTES_PER_DAY - 1, end);

    blocks.push({ start: blockStart, end: blockEnd });
    blockStart = blockEnd + 1;
  }

  return blocks;
};

/**
 * Finds unoccupied CA schedule ranges and creates daily placeholder blocks for them.
 *
 * @param {Array<Object>} events - Existing scheduler events.
 * @returns {Array<Object>} Placeholder blocks covering every schedule gap.
 */
function findScheduleGaps(events = []) {
  const intervals = [];
  let parsedCount = 0;

  // Convert events to intervals in minutes, filter out invalid events, and sort by start time
  events.forEach((event) => {
    const startMins = getEventStartInMinutes(event);
    if (startMins === null) return;

    const rawEndMins = getEventEndInMinutes(event, startMins);
    if (rawEndMins === null || rawEndMins <= startMins) return;

    // A block ending at 05:59 closes its broadcast day; the next day starts at 06:00.
    const minutesIntoBroadcastDay = (rawEndMins - BROADCAST_WEEK_START_MINUTE) % MINUTES_PER_DAY;
    const isBroadcastDayClose = minutesIntoBroadcastDay === MINUTES_PER_DAY - 1;
    const endMins = isBroadcastDayClose ? rawEndMins + 1 : rawEndMins;

    parsedCount += 1;

    // Week 1 Monday blocks that finish before 06:00 are the broadcast week's
    // closing hours, so they sit at the end of the week rather than the start.
    const isMondayCarryover = startMins < BROADCAST_WEEK_START_MINUTE && endMins <= BROADCAST_WEEK_START_MINUTE;
    const weekShift = isMondayCarryover ? MINUTES_PER_DAY * DAYS_PER_WEEK : 0;

    // Drop a portion of the event that is outside the broadcast
    // week as it belongs to an adjacent week
    const clampedStart = Math.max(startMins + weekShift, BROADCAST_WEEK_START_MINUTE);
    const clampedEnd = Math.min(endMins + weekShift, BROADCAST_WEEK_END_MINUTE);

    // Discard event if it falls entirely outside the broadcast week
    if (clampedEnd <= clampedStart) return;

    intervals.push({
      start: clampedStart,
      end: clampedEnd,
    });
  });

  // A total parse failure means the input shape doesn't match what this
  // function expects (e.g. an unparsable time format), not that the
  // schedule is genuinely empty. Fail loudly instead of treating the
  // whole broadcast week as one big gap.
  if (events.length > 0 && parsedCount === 0) {
    throw new Error('findScheduleGaps: none of the provided events could be parsed');
  }

  // Filter out intervals that are completely outside the broadcast week
  intervals.sort((a, b) => a.start - b.start);

  // Build a list of gaps between interval blocks
  const gaps = [];
  let previousEndMins = BROADCAST_WEEK_START_MINUTE;

  intervals.forEach(({ start, end }) => {
    // A gap is identified when an interval starts later than the end of the previous interval
    if (start > previousEndMins) {
      gaps.push({ start: previousEndMins, end: start });
    }
    previousEndMins = Math.max(previousEndMins, end);
  });

  // If the last interval ends before the Monday cutoff, add the trailing gap manually.
  if (previousEndMins < BROADCAST_WEEK_END_MINUTE) {
    gaps.push({ start: previousEndMins, end: BROADCAST_WEEK_END_MINUTE });
  }

  // Build placeholder blocks for each gap
  return gaps.flatMap(splitGapIntoBroadcastDays).map(({ start, end }) => {
    const startPosition = getBlockPosition(start);
    const endPosition = getBlockPosition(end);

    return {
      blockName: 'Placeholder',
      blockReference: null,
      dayOfWeek: startPosition.dayOfWeek,
      endTime: endPosition.time,
      layout: null,
      link: false,
      maxCount: 0,
      seasonID: null,
      seriesID: null,
      seriesName: null,
      sequential: false,
      startTime: startPosition.time,
      titleGroupID: null,
      titleGroupName: null,
      type: 'Placeholder',
      week: startPosition.week,
      id: crypto.randomUUID(),
    };
  });
}

export default findScheduleGaps;
